import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  BriefcaseIcon,
  EyeIcon,
  PlusCircleIcon,
  ClockIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { formatDateShort } from "@/shared/core";
import type { CaseWithDetails } from "../types/case.types";
import { Button } from "@/shared/ui";

interface ClientCasesSectionProps {
  cases: CaseWithDetails[];
  onCreateCase: () => void;
  loading?: boolean;
}

export const ClientCasesSection: React.FC<ClientCasesSectionProps> = ({
  cases,
  onCreateCase,
  loading = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      active: "bg-atlantis-100 text-atlantis-800",
      closed: "bg-tiber-100 text-tiber-800",
      archived: "bg-tiber-100 text-slate-800",
    };
    return styles[status as keyof typeof styles] || "bg-tiber-100 text-tiber-800";
  };

  const getPriorityBadge = (priority: string) => {
    const styles = {
      low: "bg-tiber-100 text-tiber-800",
      medium: "bg-tiber-100 text-tiber-800",
      high: "bg-orange-100 text-orange-800",
      urgent: "bg-red-100 text-red-800",
    };
    return (
      styles[priority as keyof typeof styles] || "bg-tiber-100 text-tiber-800"
    );
  };

  const getStats = () => {
    // Filter out 'all' status if present (shouldn't happen in real data)
    const validCases = cases.filter((c) => c.status !== "all");
    return {
      total: validCases.length,
      active: validCases.filter((c) => c.status === "active").length,
      closed: validCases.filter((c) => c.status === "closed").length,
      pending: validCases.filter((c) => c.status === "pending").length,
    };
  };

  const stats = getStats();

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-tiber-600 p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-tiber-200 rounded w-3/4"></div>
          <div className="h-4 bg-tiber-200 rounded w-1/2"></div>
          <div className="h-4 bg-tiber-200 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-tiber-600">
      {/* Header with Actions */}
      <div className="p-6 border-b border-tiber-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-tiber-900">
              {t("lawyer.clientCases.title")}
            </h2>
            <p className="text-sm text-tiber-600 mt-1">
              {t("lawyer.clientCases.subtitle")}
            </p>
          </div>
          <Button
            onClick={onCreateCase}
            variant="primary"
            leftIcon={<PlusCircleIcon className="h-5 w-5" />}
          >
            {t("lawyer.clientCases.create")}
          </Button>
        </div>

        {/* Stats Summary */}
        {cases.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-tiber-50 rounded-lg p-3">
              <p className="text-xs text-tiber-600 font-medium">
                {t("lawyer.clientCases.stats.total")}
              </p>
              <p className="text-xl font-bold text-tiber-900">{stats.total}</p>
            </div>
            <div className="bg-atlantis-50 rounded-lg p-3">
              <p className="text-xs text-atlantis-600 font-medium">
                {t("lawyer.clientCases.stats.active")}
              </p>
              <p className="text-xl font-bold text-atlantis-900">{stats.active}</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3">
              <p className="text-xs text-yellow-600 font-medium">
                {t("lawyer.clientCases.stats.pending")}
              </p>
              <p className="text-xl font-bold text-yellow-900">
                {stats.pending}
              </p>
            </div>
            <div className="bg-romance-50 rounded-lg p-3">
              <p className="text-xs text-tiber-700 font-medium">
                {t("lawyer.clientCases.stats.closed")}
              </p>
              <p className="text-xl font-bold text-juridico">{stats.closed}</p>
            </div>
          </div>
        )}
      </div>

      {/* Cases List */}
      <div className="p-6">
        {cases.length === 0 ? (
          <div className="text-center py-12">
            <BriefcaseIcon className="h-16 w-16 text-titan mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-juridico mb-2">
              {t("lawyer.clientCases.empty.title")}
            </h3>
            <p className="text-tiber-700 mb-6">
              {t("lawyer.clientCases.empty.description")}
            </p>
            <Button
              onClick={onCreateCase}
              variant="primary"
              leftIcon={<PlusCircleIcon className="h-5 w-5" />}
            >
              {t("lawyer.clientCases.createFirst")}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {cases.map((caseItem, index) => (
              <motion.div
                key={caseItem.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="border border-tiber-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <BriefcaseIcon className="h-5 w-5 text-tiber-600" />
                      <h3 className="text-lg font-semibold text-juridico">
                        {caseItem.title}
                      </h3>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-tiber-700 mb-3">
                      <span className="font-mono font-medium">
                        {caseItem.case_number}
                      </span>
                      <span className="text-titan">•</span>
                      <span className="capitalize">
                        {caseItem.practice_area}
                      </span>
                    </div>

                    {caseItem.description && (
                      <p className="text-sm text-tiber-700 mb-3 line-clamp-2">
                        {caseItem.description}
                      </p>
                    )}

                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                          caseItem.status
                        )}`}
                      >
                        {t(
                          `lawyer.cases.status.${caseItem.status}`,
                          caseItem.status
                        )}
                      </span>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityBadge(
                          caseItem.priority
                        )}`}
                      >
                        {t(
                          `lawyer.cases.priority.${caseItem.priority}`,
                          caseItem.priority
                        )}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 text-xs text-titan">
                      <div className="flex items-center gap-1">
                        <ClockIcon className="h-4 w-4" />
                        <span>
                          {t("lawyer.clientCases.created")}{" "}
                          {formatDateShort(caseItem.created_at)}
                        </span>
                      </div>
                      {caseItem.started_at && (
                        <>
                          <span className="text-titan">•</span>
                          <span>
                            {t("lawyer.clientCases.started")}{" "}
                            {formatDateShort(caseItem.started_at)}
                          </span>
                        </>
                      )}
                      {caseItem.closed_at && (
                        <>
                          <span className="text-titan">•</span>
                          <span>
                            {t("lawyer.clientCases.closed")}{" "}
                            {formatDateShort(caseItem.closed_at)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <Button
                    onClick={() =>
                      navigate(`/dashboard/lawyer/cases/${caseItem.id}`)
                    }
                    variant="primary"
                    leftIcon={<EyeIcon className="h-5 w-5" />}
                  >
                    {t("lawyer.clientCases.viewCase")}
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* View All Link */}
      {cases.length > 0 && (
        <div className="px-6 py-4 bg-romance-50 border-t border-tiber-200 text-center">
          <Button
            onClick={() => navigate("/dashboard/lawyer/cases")}
            variant="secondary"
            leftIcon={<ArrowRightIcon className="h-5 w-5" />}
          >
            {t("lawyer.clientCases.viewAll")}
          </Button>
        </div>
      )}
    </div>
  );
};
